import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem,
} from "@/components/ui/command";
import api from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { BookOpen, FileText, Package } from "lucide-react";
import { devWarn } from "@/lib/logger";

export default function CommandPalette({ open, onOpenChange }) {
  const { activeId } = useCompany();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ ledgers: [], vouchers: [], items: [] });

  useEffect(() => {
    if (!open || !activeId || !q.trim()) {
      setResults({ ledgers: [], vouchers: [], items: [] });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/companies/${activeId}/search`, { params: { q } });
        setResults(data);
      } catch (err) {
        devWarn("Search failed", err);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, activeId, open]);

  const go = (path) => {
    onOpenChange(false);
    setQ("");
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search across ledgers, vouchers, items…"
        value={q}
        onValueChange={setQ}
        data-testid="command-input"
      />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        {results.ledgers?.length > 0 && (
          <CommandGroup heading="Ledgers">
            {results.ledgers.map((l) => (
              <CommandItem key={l.id} onSelect={() => go(`/ledgers?focus=${l.id}`)}>
                <BookOpen className="w-4 h-4 mr-2 text-gray-500" />
                <span className="flex-1">{l.name}</span>
                <span className="text-xs text-gray-500">{l.group}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.vouchers?.length > 0 && (
          <CommandGroup heading="Vouchers">
            {results.vouchers.map((v) => (
              <CommandItem key={v.id} onSelect={() => go(`/vouchers`)}>
                <FileText className="w-4 h-4 mr-2 text-gray-500" />
                <span className="flex-1">{v.voucher_no} — {v.voucher_type}</span>
                <span className="text-xs text-gray-500">{v.date}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.items?.length > 0 && (
          <CommandGroup heading="Inventory">
            {results.items.map((i) => (
              <CommandItem key={i.id} onSelect={() => go(`/inventory`)}>
                <Package className="w-4 h-4 mr-2 text-gray-500" />
                <span className="flex-1">{i.name}</span>
                <span className="text-xs text-gray-500 font-mono">{i.unit}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
